"use client";

import { ComponentPropsWithoutRef, FC, ReactNode } from "react";
import { featuresThatMakesKlapBetter } from "../app/(marketing)/_lib/landing-data";
import { useMessages } from "../context/i18n";
import { rivalPage as enRivalPage } from "../dictionaries/en/rival-page";
import { trial as enTrial } from "../dictionaries/en/trial";
import AlternativeCompareTableSection, {
  AlternativeSource,
  CompareItem,
} from "./AlternativeCompareSection";
import AlternativeReasonsSection, {
  ReasonsSectionData,
} from "./AlternativeReasonsSection";
import AlternativeRelated, { RelatedLink } from "./AlternativeRelated";
import AlternativeReviewBadges, {
  ReviewBadge,
} from "./AlternativeReviewBadges";
import AlternativeTestimonialsSection, {
  AlternativeTestimonial,
} from "./AlternativeTestimonialsSection";
import AlternativeToolsTable, {
  ToolsTableData,
} from "./AlternativeToolsTable";
import FAQ, { DEFAULT_FAQ } from "./FAQ";
import Footer from "./Footer";
import Header from "./Header";
import PageHero from "./PageHero";
import TikTokVideo from "./TikTokVideo";
import TrustedBy from "./TrustedBy";
import Typography from "./Typography";
import VideoInput from "./VideoInput";
import FeaturesSection, { FeaturesBlockData } from "./blocks/FeaturesBlock";

// FAQ entries shaped both for rendering (`answer` is a `ReactNode` so it can
// embed links/buttons) AND for the FAQPage JSON-LD (`answerText` is the
// plain-text variant). Keeping both on the same record forces them to stay
// in sync at the data-file level rather than drifting between markup and
// schema.
export type ExtraFAQItem = {
  question: string;
  answer: ReactNode;
  answerText: string;
};

export type { AlternativeSource };

// All fields below are OPTIONAL so legacy alternatives data files (veed-io,
// submagic, …) keep rendering identically. Newer rival pages fill them in with
// `app/(marketing)/alternatives/_lib/template.tsx`.
export type AlternativeData = {
  name: string;
  compareItems: CompareItem[];
  // Overrides the generic "{name} alternative" page metadata. Every field
  // is optional — pages that only want to refresh the description (e.g.
  // `opus-clip.tsx`) leave `title` undefined and `generateMetadata` falls
  // back to the "Klap | The AI based alternative to X" template.
  metaOverride?: {
    title?: string;
    description?: string;
    canonical?: string;
  };
  // Replaces the default hero H1 + adds an italic freshness line
  // immediately under it.
  hero?: {
    h1?: ReactNode;
    subtitle?: ReactNode;
    freshness?: ReactNode;
  };
  // Caption under the comparison table. Defaults to saying Klap's column
  // is current and the competitor's is as of February 2025 (when those
  // tables were researched).
  compareUpdatedLabel?: string;
  // Review-platform badges (G2, Capterra, Trustpilot, …) rendered under
  // the inner `VideoInput` inside the dark "try it yourself" section.
  // Omitted on every other alternatives page.
  reviewBadges?: ReviewBadge[];
  // "Why people choose Klap over X" reasons section, inserted between
  // the comparison table and the testimonials.
  reasons?: ReasonsSectionData;
  // User-quote section with avatar, role, rating, platform logo, and
  // a deep link to the source review.
  testimonials?: AlternativeTestimonial[];
  // Appended to the default 5-item FAQ; merged in order at render time.
  extraFaq?: ExtraFAQItem[];
  // The whole FAQ, replacing the default one. Rival pages built with
  // `_lib/template.tsx` use this so their visible answers and JSON-LD share
  // one source.
  faq?: ExtraFAQItem[];
  // A short answer block right under the hero, for readers and LLMs.
  summary?: ReactNode;
  // The rival's own pages behind its column in the comparison table.
  sources?: AlternativeSource[];
  // Several tools side by side, below the reasons.
  toolsTable?: ToolsTableData;
  // Links to other rival pages and the hub, above the footer.
  related?: RelatedLink[];
  // Overrides the shared feature list, for a translated page.
  features?: FeaturesBlockData;
  // Three structured-data blocks emitted in the page head by
  // `app/(marketing)/alternatives/[slug]/page.tsx`.
  jsonLd?: {
    faqPage: object;
    softwareApp: object;
    breadcrumb: object;
  };
};

type AlternativeProps = {
  alternativeData: AlternativeData;
} & ComponentPropsWithoutRef<"div">;

const Alternative: FC<AlternativeProps> = ({ alternativeData, ...rest }) => {
  const m = useMessages("rivalPage", enRivalPage);
  const trial = useMessages("trial", enTrial);
  const heroTitle = alternativeData.hero?.h1 ?? (
    <>
      The best{" "}
      <strong className="font-semibold text-violet-600">
        {alternativeData.name} alternative
      </strong>{" "}
      for AI powered short video creation
    </>
  );
  const heroSubtitle = alternativeData.hero?.subtitle ?? (
    <>
      Klap is an AI tool like {alternativeData.name} to turn any long form
      video into viral shorts
    </>
  );
  // Merge the default 5-item FAQ with any opus-clip-specific extras so the
  // existing accordion still renders the 5 originals in place and appends
  // the new questions after them.
  const mergedFaq =
    alternativeData.faq ??
    (alternativeData.extraFaq
      ? [
          ...DEFAULT_FAQ,
          ...alternativeData.extraFaq.map(({ question, answer }) => ({
            question,
            answer,
          })),
        ]
      : undefined);

  return (
    <div {...rest} className={`${rest.className}`}>
      <Header />
      <main>
        <PageHero
          label={`${alternativeData.name} Alternative`}
          className="max-w-5xl mx-auto py-8 px-3"
          heroTitle={heroTitle}
          heroSubtitle={heroSubtitle}
          freshness={alternativeData.hero?.freshness}
          caption={trial.cta}
          cta={
            <VideoInput className="mt-12 w-full shadow-2xl max-w-lg mx-auto" />
          }
        />

        {alternativeData.summary && (
          <section className="mt-8 max-w-3xl mx-auto px-3">
            <Typography
              variant="body1"
              className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-700"
            >
              {alternativeData.summary}
            </Typography>
          </section>
        )}

        <section className="mt-16 bg-slate-950 text-white py-32 md:mx-3 md:rounded-2xl">
          <div className="max-w-5xl flex flex-col md:flex-row gap-16 mx-auto items-center justify-center px-3">
            <div className="flex-[2] ">
              <div>
                <Typography variant="h2" component="h2">
                  {m.momentsTitle}
                  <span className="text-violet-500">{m.momentsHighlight}</span>
                </Typography>
                <div className="dark">
                  <TrustedBy
                    variant="dark"
                    className="mt-8 text-slate-400 inline-flex"
                  />
                </div>
                <Typography
                  variant="body1"
                  className="text-slate-400"
                ></Typography>
                <Typography variant="body1" className="text-slate-400 mt-12">
                  {m.tryIntro}
                  <strong className="font-medium text-white">
                    {m.tryCta}
                  </strong>
                </Typography>
                <VideoInput className="mt-4 w-full shadow-2xl max-w-2xl mx-auto text-slate-950" />
                {alternativeData.reviewBadges && (
                  <AlternativeReviewBadges
                    className="mt-6"
                    badges={alternativeData.reviewBadges}
                  />
                )}
              </div>
            </div>
            <TikTokVideo
              videoId="7397517324892327200"
              title={m.tiktokVideo}
              className="max-w-[325px] shrink-0"
            />
          </div>
        </section>

        <AlternativeCompareTableSection
          className="my-32 w-full items-center px-3 lg:px-12 flex-1 max-w-7xl mx-auto"
          comparee={alternativeData.name}
          compareItems={alternativeData.compareItems}
          updatedLabel={alternativeData.compareUpdatedLabel}
          sources={alternativeData.sources}
        />

        {alternativeData.reasons && (
          <AlternativeReasonsSection
            data={alternativeData.reasons}
            className="my-32 w-full px-3 lg:px-12 max-w-5xl mx-auto"
          />
        )}

        {alternativeData.toolsTable && (
          <AlternativeToolsTable
            data={alternativeData.toolsTable}
            className="my-32 w-full px-3 lg:px-12 max-w-7xl mx-auto"
          />
        )}

        {alternativeData.testimonials &&
          alternativeData.testimonials.length > 0 && (
            <AlternativeTestimonialsSection
              testimonials={alternativeData.testimonials}
              className="my-32 w-full px-3 lg:px-12 max-w-6xl mx-auto"
            />
          )}

        <FeaturesSection
          data={alternativeData.features ?? featuresThatMakesKlapBetter}
          title={m.featuresAttr}
          className="my-64 w-full items-center px-3 lg:px-12 flex-1 max-w-7xl text-center mx-auto"
        ></FeaturesSection>
        <FAQ className="mx-auto w-full max-w-5xl my-64" faq={mergedFaq} />
        {alternativeData.related && (
          <AlternativeRelated
            links={alternativeData.related}
            className="my-32 w-full px-3 max-w-5xl mx-auto"
          />
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Alternative;
